<template>
  <!-- 
        Dynamic Form Version 0.1 (Order Boat Form only)
        An automatically generated form with validations,
        custom error messages, labels, multiple field types with
        a flexible layout that allows any number of fields and field
        types. In future versions the API endpoint should also be a prop.

        ----- How to use -----
        The component consumes an object with this structure:
        field_name : { property_1: value, property_2: value }

        Available properties (case sensitive):
        type -> defines the input type like text, number, etc
        label -> the label above the input
        errorMessage -> the error message to be displayed below the field
        placeholder -> the input placeholder value
        options -> options for select and multiselect field types

        Note: in case of multiselect and select, you must provide options.

        Debugging notes:
        The fields are v-modelled to array properties.
        Custom field validations can be added on orderBoat()

        WIP:
        defaultSelected - on select and multiselect, select by default the component prop
    -->
  <section name="order-boat" class="order_boat">
    <article>
      <section
        id="order-boat-modal"
        name="order-boat-modal"
        class="order-boat-modal"
        v-if="isModalOpen"
      >
        <article
          class="modal-card h-full w-full overflow-y-scroll lg:overflow-hidden"
        >
          <div class="sushiboat-form overflow-y-auto h-full">
            <button @click="close()" class="float-right text-orange text-2xl">
              x
            </button>
            <div>
              <div
                class="contact-container dots w-full"
                style="box-shadow: none"
              >
                <h3 class="order-boat-title">{{ formTitle }}</h3>
                <p class="order-boat-description">{{ formDescription }}</p>
                <span class="text-danger error hidden">{{ apiError }}</span>

                <div class="lg:flex flex-wrap">
                  <div
                    v-for="(field, key) in fields"
                    :key="key"
                    :class="
                      'input-container w-full lg:pr-4 mt-4 input-error ' +
                      (field.type != 'textarea' ? 'lg:w-1/3' : '')
                    "
                  >
                    <label class="capitalize">{{ field.label }}*</label>
                    <div class="input-box">
                      <div class="icon name-icon"></div>
                      <div
                        v-if="field.type == 'multiselect'"
                        class="select-container"
                      >
                        <multiselect
                          :options="field.options"
                          :is-searchable="true"
                          :api-search-enabled="true"
                          :name="field.name + ' multiselect'"
                        >
                        </multiselect>
                      </div>
                      <VuePhoneNumberInput
                        v-else-if="field.type == 'phone'"
                        @update="(payload) => updatePhoneCode(payload)"
                        class="phone-input"
                        v-model="phone"
                        :no-validator-state="true"
                        default-country-code="PT"
                        :translations="{ phoneNumberLabel: 'Telefone' }"
                      />
                      <select
                        v-else-if="field.type == 'select'"
                        :name="field.name"
                        v-model="field.inputData"
                      >
                        <option
                          v-for="(option, key) in field.options"
                          :key="key"
                          :value="option"
                          :selected="field.placeholder == option"
                        >
                          {{ option == "big" ? "Grande" : "Pequeno" }}
                          <!-- hard-coded -->
                        </option>
                      </select>
                      <textarea
                        style="width: 100%"
                        v-else-if="field.type == 'textarea'"
                        :required="field.required ? field.required : false"
                        v-model="field.inputData"
                        class="form-control"
                        :placeholder="field.placeholder"
                      ></textarea>
                      <input
                        v-else-if="field.type == 'date'"
                        :min="tomorrow"
                        :required="field.required ? field.required : false"
                        v-model="field.inputData"
                        :type="field.type"
                        class="form-control"
                        :placeholder="field.placeholder"
                      />
                      <input
                        v-else
                        :required="field.required ? field.required : false"
                        v-model="field.inputData"
                        :type="field.type"
                        class="form-control"
                        :placeholder="field.placeholder"
                      />
                    </div>
                    <p v-if="field.errorMessage" class="text-danger error">
                      {{ field.errorMessage }}
                    </p>
                  </div>
                </div>
                <span>*campos obrigatórios</span>
                <div
                  class="flex flex-col lg:flex-row justify-center items-center"
                >
                  <div
                    class="my-8 lg:mb-0 inline-flex flex-col-reverse lg:block"
                  >
                    <button
                      class="btn--outlined border-none"
                      type="button"
                      @click="close()"
                    >
                      Cancelar
                    </button>
                    <button class="btn" type="button" @click="orderBoat">
                      Fazer Pedido
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </section>
    </article>
  </section>
</template>

<script>
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";

export default {
  components: { VuePhoneNumberInput },
  props: {
    isModalOpen: { type: Boolean, required: true, default: false },
    formFields: { type: Object, required: true, default: () => {} },
    formTitle: { type: String },
    formDescription: { type: String },
    defaultSelected: { type: Object },
  },
  methods: {
    orderBoat(e) {
      e.preventDefault();
      this.apiError = "";
      this.clearErrors();
      let payload = {};
      for (let [field, data] of Object.entries(this.fields)) {
        if (field == "phone") {
          payload[field] = this.phone;
          payload["phonecode"] = this.phoneCode;
        } else {
          let value = data.inputData;
          if (field == "date") {
            value = this.formatDate(value);
          }
          // custom field validators end here
          payload[field] = value;
        }
      }
      this.$axios
        .post("/api/sushi-boat/request", payload)
        .then((response) => {
          this.close();
        })
        .catch((error) => {
          this.apiError = error.response.data.message;

          /* when the api sends errors for specific fields, 
                    replace the errorMessage with the error from the api */
          for (const [key, value] of Object.entries(
            error.response.data.errors
          )) {
            if (key == "phonecode") {
              this.fields["phone"]["errorMessage"] = "Codigo país: " + value[0];
            } else {
              this.fields[key]["errorMessage"] = value[0];
            }
          }
        });
    },
    formatDate(dateString) {
      let date = new Date(dateString).toLocaleString("en-GB").split(",")[0];
      return date;
    },
    updatePhoneCode(payload) {
      if (payload.countryCallingCode) {
        this.phoneCode = payload.countryCallingCode;
      } else {
        if (payload.countryCode == "PT") {
          this.phoneCode = "351";
        }
      }
    },
    clearErrors() {
      for (let [field, data] of Object.entries(this.fields)) {
        data.errorMessage = false;
      }
    },
    resetFields() {
      this.phone = null;
      this.phonecode = null;
      for (let [field, data] of Object.entries(this.fields)) {
        data.errorMessage = false;
        data.inputData = null;
      }
    },
    close() {
      this.resetFields();
      this.$emit("closeModal");
    },
  },
  mounted() {
    //pass prop fields into state
    for (let [fieldName, fieldValues] of Object.entries(this.formFields)) {
      fieldValues.inputData = "";
      this.fields[fieldName] = fieldValues;
    }
  },
  data: () => ({
    //fields to v-model and post
    fields: {},
    phone: null,
    phoneCode: null,
    apiError: "",
    tomorrow: new Date(new Date().setDate(new Date().getDate() + 1))
      .toISOString()
      .substr(0, 10),
  }),
};
</script>

